/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useDebounce.ts
 * PURPOSE: Custom React hooks for state management
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 *
 * @business-context
 * - Domain: React Hooks, State Management, Vietnamese F&B Business Logic
 * - Impact: Code Reusability, State Consistency, Developer Productivity
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: React Hooks Pattern, State Management, API Integration
 * - Dependencies: React 18, TypeScript, API Services
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/FRONTEND-BACKEND-GAP-ANALYSIS-SPRINT10-13.md
 *  */

import { useState, useEffect } from 'react';

/**
 * Custom hook that debounces a value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 500ms)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if the value changes before the delay has passed
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default useDebounce;